// FeedbackContext.js
import React, { createContext, useState, useEffect, useContext } from "react";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { useCopilotDataContext } from "./CopilotDataContextV2";
import {
  build_default_State,
  filter_data as buildFilteredData,
} from "reports/Audit/data_navigation/calculate_data";
import {
  SG_GET_TASK_SCHEDULES,
  SG_GET_TASK_TEMPLATES,
  SG_GET_TASK_TEMPLATE_SELECT,
  SG_FETCH_TASK_SCHEDULE,
  CLEAR_SELECTED_EMPLOYEE_FILE_UPLOAD,
  CLEAR_ADDED_FILE,
  SET_NEW_FILTERS
} from "constants/actions";

/**
 * Parses a pulse survey object (like the one provided) and returns a
 * structured feedback string.
 *
 * @param {Object} pulseSurvey - The entire pulse survey object.
 * @returns {String} A formatted string of user feedback.
 */
function parsePulseSurveyResponses(pulseSurvey) {
  // Safety check: if no task_responses, return early.
  if (!pulseSurvey?.task_responses || pulseSurvey.task_responses.length === 0) {
    return "No task responses found.";
  }

  let feedbackString = "";

  pulseSurvey.task_responses.forEach((responseObj) => {
    // Each responseObj corresponds to one user (or one response session).
    // We can pull out 'employee' or 'email' if we want to note it in our feedback as well.
    const { task_response, employee } = responseObj;
    const userEmail = task_response?.email;

    // If you want to identify which user's feedback you are reporting:
    //   feedbackString += `=== Feedback from user ID ${employee} (${userEmail}) ===\n\n`;

    // The main array of question-response pairs is in task_list
    const taskList = task_response?.task_list;
    if (!taskList || taskList.length === 0) {
      feedbackString += "No questions answered.\n\n";
      return; // proceed to next responseObj
    }

    // Loop each question/response in the task_list
    taskList.forEach((item) => {
      const { question, response, follow_up } = item;

      if (typeof response === "number") {
        // This is presumably a "quantitative" question
        feedbackString += `User was asked quantitative question: ${question}\n\n`;
        feedbackString += `User scored this question: ${response}\n\n`;

        // If there's a follow-up for the quantitative question
        if (follow_up && follow_up.question && follow_up.response) {
          feedbackString += `User was asked follow-up question: ${follow_up.question}\n`;
          feedbackString += `User responded to the follow-up question with: ${follow_up.response}\n\n`;
        }
      } else {
        // This is presumably an "open text" question
        feedbackString += `User provided feedback for the following question: ${question}\n\n`;
        feedbackString += `User responded with: ${response}\n\n`;

        // If there's a follow-up for the open-text question
        if (follow_up && follow_up.question && follow_up.response) {
          feedbackString += `User was asked follow-up question: ${follow_up.question}\n`;
          feedbackString += `User responded to the follow-up question with: ${follow_up.response}\n\n`;
        }
      }
    });

    //   feedbackString += `=== End of feedback from user ID ${employee} ===\n\n`;
  });

  return feedbackString.trim();
}

// Helper function to calculate factor average
const calculateFactorAverage = (questions) => {
  let total = 0;
  let count = 0;

  questions.forEach((question) => {
    let response = question?.response;
    if (typeof response !== "number" || Number.isNaN(response)) {
      return;
    }
    if (question?.reverse) {
      // Invert the score if the reverse is true
      response = 10 - response;
    }
    total += response;
    count++;
  });

  // Avoid division by zero
  if (count === 0) {
    return null;
  }
  const average = (total / count) * 10;
  if (!Number.isFinite(average)) {
    return null;
  }
  return Number.isInteger(average) ? average : Number(average.toFixed(2));
};

export const FeedbackContext = createContext();

export const useFeedbackContext = () => useContext(FeedbackContext);

export const FeedbackProvider = ({ children }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [feedback, setFeedback] = useState("");
  const [feedbackLength, setFeedbackLength] = useState(0);
  const [feedbackLoading, setFeedbackLoading] = useState(true);
  const [emptyData, setEmptyData] = useState(false);
  const [initialLoad, setInitialLoad] = useState(false);
  const [selectedPulse, setSelectedPulse] = useState(null);
  const [pulseData, setPulseData] = useState(null);
  const [selectedFactors, setSelectedFactors] = useState([]);
  const [selectedOutcomes, setSelectedOutcomes] = useState([]);
  const [factorCardData, setFactorCardData] = useState([]);
  const [selectedPulses, setSelectedPulses] = useState([]);
  const [fetchedPulsesData, setFetchedPulsesData] = useState({}); // Store multiple fetched pulse data by ID
  const [outcomeCardData, setOutcomeCardData] = useState([]);
  const [filteredPulses, setFilteredPulses] = useState([]);
  const [selectedCustomQuestions, setSelectedCustomQuestions] = useState([]);
  const [customQuestionsCardData, setCustomQuestionsCardData] = useState([]);
  const [selectedSurveys, setSelectedSurveys] = useState([]);
  const [surveyCardData, setSurveyCardData] = useState([]);
  const filteredData = useSelector((state) => state.audit?.filtered_data);
  const { shouldUseCustomDataset = false } = useCopilotDataContext() || {};
  const coreData = useSelector((state) => state.audit?.core_data);
  const questionStructureFromRedux = useSelector(
    (state) => state.audit?.survey_version,
    shallowEqual
  );
  const surveyStructure = useSelector(
    (state) => state.surveystructure?.survey_structure?.[0],
    shallowEqual
  );
  const navState = useSelector((state) => state.audit?.nav);

  const selectedFilters = useSelector(
    (state) => state.audit?.new_filters,
    shallowEqual
  );

  const get_auth = useSelector((state) => state.auth);

  // Add selector for custom dataset (same as CopilotDataContextV2)
  const existingRawData = useSelector((state) => state.audit?.raw_data);
  const fetchedPulse = useSelector(
    (state) => state.task_templates?.fetched_scheduled_task
  );

  const pulseSurveys = useSelector(
    (state) => state.task_templates.scheduled_tasks?.results
  );

  const hasCustomDataset =
    shouldUseCustomDataset &&
    Array.isArray(existingRawData) &&
    existingRawData?.[0]?.datasetMetadata;
  const isOtpAiRoute = location.pathname.toLowerCase().startsWith("/app/otp/ai");

  const filtersApplied =
    Array.isArray(selectedFilters) && selectedFilters.length > 0;

  const normalizedFilteredData = React.useMemo(() => {
    if (!Array.isArray(filteredData)) return [];
    if (Array.isArray(filteredData[0])) {
      return filteredData[0];
    }
    return filteredData;
  }, [filteredData]);

  const hasReduxFilteredResponses = React.useMemo(() => {
    if (!Array.isArray(filteredData)) return false;
    if (Array.isArray(filteredData[0])) {
      return Array.isArray(filteredData[0]) && filteredData[0].length > 0;
    }
    return filteredData.length > 0;
  }, [filteredData]);

  const computedSurveyStructure = React.useMemo(() => {
    if (surveyStructure) return surveyStructure;
    if (coreData?.categories?.[0] && coreData?.questions) {
      return {
        ...coreData.categories[0],
        categories: coreData.categories[0].categories,
        questions: coreData.questions,
      };
    }
    return null;
  }, [surveyStructure, coreData]);

  const customDatasetFilteredResult = React.useMemo(() => {
    if (
      !hasCustomDataset ||
      !computedSurveyStructure ||
      !Array.isArray(existingRawData)
    ) {
      return null;
    }
    const navToUse =
      navState && Object.keys(navState || {}).length > 0
        ? navState
        : build_default_State(computedSurveyStructure);
    if (!navToUse) return null;
    try {
      return buildFilteredData(existingRawData, navToUse, computedSurveyStructure);
    } catch (error) {
      console.error("Failed to derive filtered data for custom dataset", error);
      return null;
    }
  }, [
    hasCustomDataset,
    computedSurveyStructure,
    existingRawData,
    navState,
  ]);

  const customDatasetFilteredData = React.useMemo(() => {
    const filtered = customDatasetFilteredResult?.filter_data;
    if (!Array.isArray(filtered)) return [];
    return filtered;
  }, [customDatasetFilteredResult]);

  const customDatasetNormalizedResponses = React.useMemo(() => {
    if (!hasCustomDataset) return [];
    if (!Array.isArray(customDatasetFilteredData)) return [];
    if (Array.isArray(customDatasetFilteredData[0])) {
      return customDatasetFilteredData[0].filter(Boolean);
    }
    return customDatasetFilteredData.filter(Boolean);
  }, [hasCustomDataset, customDatasetFilteredData]);

  const customDatasetResponses = React.useMemo(() => {
    if (!hasCustomDataset) return [];
    if (customDatasetNormalizedResponses.length > 0) {
      return customDatasetNormalizedResponses;
    }
    const responses = existingRawData?.[0]?.responses;
    if (!Array.isArray(responses)) return [];
    return responses.filter(Boolean);
  }, [
    hasCustomDataset,
    customDatasetNormalizedResponses,
    existingRawData,
  ]);


  const effectiveResponses = React.useMemo(() => {
    if (hasReduxFilteredResponses) {
      return normalizedFilteredData;
    }

    if (!filtersApplied && customDatasetResponses.length > 0) {
      return customDatasetResponses;
    }

    return [];
  }, [
    hasReduxFilteredResponses,
    normalizedFilteredData,
    filtersApplied,
    customDatasetResponses,
  ]);

  const customDatasetSurveyEntry = React.useMemo(() => {
    if (!hasCustomDataset) return null;
    const rawEntry = existingRawData?.[0];
    const metadata = rawEntry?.datasetMetadata;
    if (!metadata) return null;

    const pickFirst = (...values) =>
      values.find(
        (value) => typeof value === "string" && value.trim().length > 0
      ) || null;

    const rawId =
      metadata?.selectedDebriefId ??
      metadata?.debriefScheduleId ??
      metadata?.selectedSortOrder ??
      metadata?.source ??
      metadata?.id ??
      metadata?.datasetId;

    const id =
      typeof rawId === "string"
        ? rawId
        : rawId != null
          ? `custom-${rawId}`
          : "custom-dataset";

    const title =
      pickFirst(
        metadata?.surveyName,
        metadata?.groupTitle,
        metadata?.datasetName,
        metadata?.seriesName,
        metadata?.selectionLabel
      ) || "Custom dataset";

    const lastUpdated =
      metadata?.timestamp ||
      metadata?.completionDate ||
      metadata?.updatedAt ||
      null;

    return {
      type: "survey",
      id,
      title,
      metadata: {
        ...metadata,
        surveyName: metadata?.surveyName,
        groupTitle: metadata?.groupTitle,
        completionDate: lastUpdated,
        isIndividual:
          metadata?.isIndividual ||
          metadata?.selectionType === "debrief",
      },
      responses: Array.isArray(customDatasetResponses)
        ? customDatasetResponses
        : Array.isArray(rawEntry?.responses)
          ? rawEntry.responses
          : [],
      lastUpdated,
      isCustomDataset: true,
    };
  }, [hasCustomDataset, existingRawData, customDatasetResponses]);

  // Don't add customDatasetSurveyEntry to selectedSurveys
  // It causes duplicates in the UI - the custom dataset is already shown via displaySelectedSources
  // useEffect removed to prevent duplicates

  useEffect(() => {
    if (hasCustomDataset) return;
    setSelectedSurveys((prev) =>
      prev.filter((survey) => !survey?.isCustomDataset)
    );
  }, [hasCustomDataset]);

  const effectiveSelectedSurveys = React.useMemo(() => {
    const list = [...selectedSurveys];
    if (customDatasetSurveyEntry) {
      const exists = list.some((survey) => survey.id === customDatasetSurveyEntry.id);
      if (!exists) {
        list.push(customDatasetSurveyEntry);
      }
    }
    return list;
  }, [selectedSurveys, customDatasetSurveyEntry]);

  useEffect(() => {
    dispatch({ type: SG_GET_TASK_TEMPLATE_SELECT });
    dispatch({ type: SG_GET_TASK_SCHEDULES });
    dispatch({ type: SG_GET_TASK_TEMPLATES });
  }, [dispatch]);

  // Store fetched pulse data when it arrives from Redux
  useEffect(() => {
    if (fetchedPulse && fetchedPulse.id) {
      setFetchedPulsesData((prev) => ({
        ...prev,
        [fetchedPulse.id]: fetchedPulse,
      }));
    }
  }, [fetchedPulse]);

  const activeQuestionStructure = questionStructureFromRedux || computedSurveyStructure;

  const factorLabels = React.useMemo(() => {
    const structure = activeQuestionStructure;
    if (!structure?.questions?.dimensions) return [];

    let idCounter = 1;

    return structure.questions.dimensions.map((dim, dimensionIndex) => ({
      dimensionIndex,
      dimensionTitle: dim.title,
      factors:
        dim.factors?.map((factor, factorIndex) => ({
          title: factor.title,
          level: 2,
          id: idCounter++,
          dimension: dimensionIndex,
          factor: factorIndex,
        })) || [],
    }));
  }, [activeQuestionStructure]);

  // Function to get custom questions from the survey structure
  const getCustomQuestions = React.useMemo(() => {
    const structure = activeQuestionStructure;
    if (!structure?.questions?.comments) return [];

    return structure.questions.comments.map((comment, index) => ({
      id: comment?.id || index,
      title: comment.question,
      level: 4,
      type: "comments",
      question: comment.question,
    }));
  }, [activeQuestionStructure]);

  useEffect(() => {
    // Add the type: 'factor' to factorCardData
    setFactorCardData(
      selectedFactors?.map((factor) => ({
        ...factor,
        type: "culture",
      }))
    );
  }, [selectedFactors]);

  // Sync selectedFactors and selectedOutcomes to Redux selectedFilters so filter_data can apply filtering
  useEffect(() => {
    // Get current filters from Redux, excluding existing factor and outcome filters
    const nonFilterFilters = (Array.isArray(selectedFilters) ? selectedFilters : []).filter(
      (filter) => filter.type_of !== "factor" && filter.type_of !== "outcome"
    );

    // Convert selectedFactors to the format expected by filter_data
    const factorFilters = selectedFactors.map((factor) => ({
      type_of: "factor",
      dimensionIndex: factor.dimension,
      id: factor.factor, // The factor index within the dimension
      name: factor.title,
    }));

    // Convert selectedOutcomes to the format expected by filter_data
    const outcomeFilters = selectedOutcomes.map((outcome) => ({
      type_of: "outcome",
      id: outcome.id,
      name: outcome.title,
    }));

    // Combine non-filter filters with new factor and outcome filters
    const updatedFilters = [...nonFilterFilters, ...factorFilters, ...outcomeFilters];

    // Check if the filters actually changed to avoid unnecessary updates
    const currentFactorFilters = (Array.isArray(selectedFilters) ? selectedFilters : []).filter(
      (filter) => filter.type_of === "factor"
    );
    const currentOutcomeFilters = (Array.isArray(selectedFilters) ? selectedFilters : []).filter(
      (filter) => filter.type_of === "outcome"
    );

    const factorFiltersChanged =
      currentFactorFilters.length !== factorFilters.length ||
      factorFilters.some((newFilter, index) => {
        const currentFilter = currentFactorFilters[index];
        return !currentFilter ||
          currentFilter.dimensionIndex !== newFilter.dimensionIndex ||
          currentFilter.id !== newFilter.id;
      });

    const outcomeFiltersChanged =
      currentOutcomeFilters.length !== outcomeFilters.length ||
      outcomeFilters.some((newFilter, index) => {
        const currentFilter = currentOutcomeFilters[index];
        return !currentFilter || currentFilter.id !== newFilter.id;
      });

    // Only update if filters changed
    if (factorFiltersChanged || outcomeFiltersChanged) {
      dispatch({
        type: SET_NEW_FILTERS,
        payload: updatedFilters,
      });
    }
  }, [selectedFactors, selectedOutcomes, dispatch, selectedFilters]);

  useEffect(() => {
    if (Array.isArray(selectedCustomQuestions[0])) {
      setCustomQuestionsCardData([
        {
          type: "custom",
          id: "custom-questions", // Ensure id is a string as expected by DataSourceCard
          title: `Custom Questions (${selectedCustomQuestions[0].length})`, // Show count of selected questions
          selections: selectedCustomQuestions[0],
          count: selectedCustomQuestions[0].length,
        },
      ]);
    } else if (selectedCustomQuestions && selectedCustomQuestions.length > 0) {
      // Add the type: 'custom' to customQuestionsCardData and ensure proper structure
      setCustomQuestionsCardData([
        {
          type: "custom",
          id: "custom-questions", // Ensure id is a string as expected by DataSourceCard
          title: `Custom Questions (${selectedCustomQuestions.length})`, // Show count of selected questions
          selections: selectedCustomQuestions,
          count: selectedCustomQuestions.length,
        },
      ]);
      // Sometimes for some reason, selectedCustomQuestions is an array of arrays.
      // We need to flatten it.
    } else {
      setCustomQuestionsCardData([]);
    }
  }, [selectedCustomQuestions]);

  useEffect(() => {
    // Add the type: 'survey' to surveyCardData
    setSurveyCardData(
      selectedSurveys?.map((survey) => ({
        ...survey,
        type: "survey",
      }))
    );
  }, [selectedSurveys]);

  useEffect(() => {
    if (pulseSurveys?.length > 0) {
      // Filter pulses based on user access
      // if the user created the pulse, show it.
      // If the users id is in the employee_access array, show it.
      // Get the user's id from the coreData?.permissions?.id
      const userCreatedPulses = pulseSurveys.filter(
        (pulse) => pulse.created_by === get_auth?.employee?.id
      );
      const userAccessedPulses = pulseSurveys.filter((pulse) =>
        pulse.employee_access.includes(get_auth?.employee?.id)
      );
      setFilteredPulses([...userCreatedPulses, ...userAccessedPulses]);
    }
  }, [pulseSurveys, get_auth]);

  const handleRemoveFactor = (factor) => {
    // Remove the factor from the selectedFactors array.
    // The factor object should be removed by matching factor.id, factor.dimension, and factor.factor.
    setSelectedFactors(
      selectedFactors.filter(
        (f) =>
          f.id !== factor.id ||
          f.dimension !== factor.dimension ||
          f.factor !== factor.factor
      )
    );
  };

  const handleRemoveAllFactors = () => {
    setSelectedFactors([]);
  };

  const handleRemoveOutcome = (outcome) => {
    setSelectedOutcomes(selectedOutcomes.filter((o) => o.id !== outcome.id));
  };

  const handleRemoveAllOutcomes = () => {
    setSelectedOutcomes([]);
  };

  const handleRemoveCustomQuestion = (question) => {
    // Ensure we're working with a proper array
    const questionsArray = Array.isArray(selectedCustomQuestions)
      ? [...selectedCustomQuestions]
      : Object.values(selectedCustomQuestions || {}).filter(
          (item) => typeof item === "object"
        );

    // Filter out the question to remove
    const updatedQuestions = questionsArray.filter((q) => {
      // Handle both array and object structures
      if (Array.isArray(q)) {
        return !q.some((item) => item.id.toString() === question.id.toString());
      }
      return q.id.toString() !== question.id.toString();
    });

    setSelectedCustomQuestions(updatedQuestions);
  };

  const handleRemoveAllCustomQuestions = () => {
    // This is just like removing a pulse.
    // We need to remove the custom questions from the selectedCustomQuestions array.
    // We also need to remove the custom questions from the customQuestionsCardData array.
    setSelectedCustomQuestions([]);
    setCustomQuestionsCardData([]);
  };

  useEffect(() => {
    // Add the type: 'outcome' to outcomeCardData
    setOutcomeCardData(
      selectedOutcomes?.map((outcome) => ({
        ...outcome,
        type: "outcome",
      }))
    );
  }, [selectedOutcomes]);

  const generateDefaultGroupStructure = (
    outcomeQ,
    isFrench = false,
    pillarOnly = false
  ) => {
    const structure = activeQuestionStructure;
    if (!structure?.questions?.dimensions) return [];

    let idCounter = 0;
    const defaultGroupStructure = [];

    // Overall Culture
    if (!pillarOnly) {
      defaultGroupStructure.push({
        title: isFrench ? "Culture Générale" : "Overall Culture",
        level: 0,
        id: -1,
      });
    }

    // Dimensions & Factors
    structure.questions.dimensions?.forEach((dim, dimensionIndex) => {
      if (!pillarOnly) {
        defaultGroupStructure.push({
          title: isFrench ? dim.fr || dim.title : dim.title,
          level: 1,
          id: idCounter++,
          dimension: dimensionIndex,
        });
      }

      dim.factors?.forEach((factor, factorIndex) => {
        defaultGroupStructure.push({
          title: isFrench ? factor.fr || factor.title : factor.title,
          level: 2,
          id: idCounter++,
          dimension: dimensionIndex,
          factor: factorIndex,
        });
      });
    });

    // Outcomes
    if (outcomeQ?.questions?.length > 0) {
      defaultGroupStructure.push({
        title: "Outcomes",
        level: 1,
      });
      outcomeQ.questions.forEach((oq) => {
        defaultGroupStructure.push({
          title: oq.name,
          id: oq.id,
          level: 3,
          type: "outcome",
        });
      });
    }

    // Custom Questions
    if (structure?.questions?.comments?.length > 0) {
      defaultGroupStructure.push({
        title: "Custom Questions",
        level: 1,
      });
      structure.questions.comments.forEach((c, i) => {
        defaultGroupStructure.push({
          title: c.question,
          id: c?.id || i,
          level: 4,
          type: "comments",
        });
      });
    }

    return defaultGroupStructure;
  };

  // Helper to determine the group list
  const getGroupList = (
    selectedGroup,
    defaultGroupStructure,
    groupStructure,
    isOTP
  ) => {
    if (
      selectedGroup.length > 0 &&
      selectedGroup[0]?.title !== "Overall Culture" &&
      selectedGroup[0]?.id !== -1
    ) {
      return selectedGroup;
    }
    // Return default structure to include all factors with scores
    // Previously returned [] for non-OTP, which skipped score processing
    return defaultGroupStructure || [];
  };

  const handleSelectPulse = (pulseId) => {
    // If the pulse is already selected, return;
    // If the pulse is not selected, add it to the selectedPulses array.
    // The object should have the { id, type } of the pulse.
    if (selectedPulses.some((p) => p.id === pulseId)) return;
    // Find the pulse so we can get the title.
    const pulse = pulseSurveys.find((p) => p.id === pulseId);
    setSelectedPulses([
      ...selectedPulses, // Allow multiple pulses since they only contain raw feedback
      { id: pulseId, type: "pulse", title: pulse?.name || "N/A" },
    ]);

    // Get the feedback for the pulse, and determine
    // if there is more than 2 responses.
    // We need to call the API to get the feedback for the pulse.
    dispatch({ type: SG_FETCH_TASK_SCHEDULE, payload: pulseId });
  };

  const handleSelectCustomQuestion = (questionObj) => {
    // If the question is already selected, return
    if (
      selectedCustomQuestions?.some(
        (q) => q?.id?.toString() === questionObj?.id?.toString()
      )
    )
      return;

    // Ensure we're always working with an array
    const currentQuestions = Array.isArray(selectedCustomQuestions)
      ? [...selectedCustomQuestions]
      : Object.values(selectedCustomQuestions || {}).filter(
          (item) => typeof item === "object"
        );

    // Add the new question to the array
    setSelectedCustomQuestions([...currentQuestions, questionObj]);
  };

  const handleSelectSurvey = (surveyData) => {
    // Use functional update to avoid race conditions
    setSelectedSurveys((prevSurveys) => {
      // Check if survey is already selected by ID
      if (prevSurveys.some((s) => s.id === surveyData.id)) {
        console.log('Survey already in selectedSurveys, skipping:', surveyData.id);
        return prevSurveys;
      }

      // Also check by originalDrawerId if present
      if (surveyData.metadata?.originalDrawerId) {
        const existsByDrawerId = prevSurveys.some(
          (s) => s.metadata?.originalDrawerId === surveyData.metadata.originalDrawerId
        );
        if (existsByDrawerId) {
          console.log('Survey already in selectedSurveys (by drawerId), skipping:', surveyData.metadata.originalDrawerId);
          return prevSurveys;
        }
      }

      // Add the survey to the selected surveys array
      return [...prevSurveys, surveyData];
    });
  };

  const handleRemoveSurvey = (surveyId) => {
    // Remove the survey from selectedSurveys
    setSelectedSurveys(selectedSurveys.filter((s) => s.id !== surveyId));

    // If this was a drawer-loaded survey, clear the Redux data
    const metadata = existingRawData?.[0]?.datasetMetadata;
    if (metadata?.sourceLocation === 'drawer') {
      console.log("Clearing drawer-loaded survey data from Redux");
      dispatch({ type: "SET_RAW_DATA", payload: [] });
      dispatch({ type: "SET_FILTERED_DATA", payload: [] });
    }
  };

  const handleRemoveAllSurveys = () => {
    setSelectedSurveys([]);
    setSurveyCardData([]);
  };

  // Note: Pulse survey feedback length is now handled in the main useEffect below

  const handleRemovePulse = (pulseId) => {
    // Remove the pulse from selectedPulses
    setSelectedPulses(selectedPulses.filter((p) => p.id !== pulseId));

    // Remove the fetched pulse data from state
    setFetchedPulsesData((prev) => {
      const newData = { ...prev };
      delete newData[pulseId];
      return newData;
    });

    // Reset feedback length if no pulses are selected
    if (selectedPulses.length === 1) {
      // If this was the last pulse
      setFeedbackLength(0);
    }

    // Reset feedback if no pulses are selected
    if (selectedPulses.length === 1) {
      // If this was the last pulse
      setFeedback("");
    }
  };

  // Helper to filter feedback and questions by group level
  const filterByGroupLevel = (group, feedback, questions) => {
    let filteredFeedback = [...feedback];
    let filteredQuestions = [...questions];

    switch (group.level) {
      case 0:
        // Overall Culture - no specific filtering needed since we skip feedback generation
        break;
      case 1:
        filteredFeedback = filteredFeedback.filter(
          (f) =>
            f.feedback.id === group.dimension ||
            f.feedback?.dimension === group.dimension
        );
        filteredQuestions = filteredQuestions.filter((q) =>
          q.questions.some((question) => question.id === group.dimension)
        );
        break;
      case 2:
        filteredFeedback = filteredFeedback.filter(
          (f) =>
            (f.feedback.id === group.dimension ||
              f.feedback?.dimension === group.dimension) &&
            f.feedback.factor === group.factor
        );
        filteredQuestions = filteredQuestions.map((q) => ({
          ...q,
          questions: q.questions.filter(
            (question) =>
              question.id === group.dimension &&
              question.factor === group.factor
          ),
        }));
        break;
      case 3:
        filteredFeedback = filteredFeedback.filter(
          (f) =>
            f.type === "outcome" && Number(f.feedback.q) === Number(group.id)
        );
        filteredQuestions = filteredQuestions.filter((q) =>
          q.questions.some(
            (question) => Number(question.q) === Number(group.id)
          )
        );
        break;
      case 4:
        // Handle custom questions (level 4)
        filteredFeedback = filteredFeedback.filter(
          (f) => f.type === "comments" && f.id === group.id
        );

        filteredQuestions = filteredQuestions.filter((q) =>
          q.questions.some((question) => question.q === group.id)
        );
        break;
      default:
        break;
    }

    return { filteredFeedback, filteredQuestions };
  };

  const newChatClicked = () => {
    // If the user clicks on a new chat, we need to reset the feedback data.
    setFeedback("");
    setFeedbackLength(0);
    setFeedbackLoading(true);
    setEmptyData(false);
    setInitialLoad(false);
    // Clear the selected factors and outcomes
    setSelectedFactors([]);
    setSelectedOutcomes([]);
    setSelectedPulses([]);
    setFetchedPulsesData({}); // Clear all fetched pulse data
    setSelectedCustomQuestions([]);
    setOutcomeCardData([]);
    setFactorCardData([]);
    setCustomQuestionsCardData([]);
    // Clear all file upload related state
    dispatch({ type: CLEAR_SELECTED_EMPLOYEE_FILE_UPLOAD });
    dispatch({ type: CLEAR_ADDED_FILE });
  };

  // Helper to generate feedback text
  const generateFeedbackText = (groupFeedback, isOTP, coreData) => {
    const sanitizeQuestion = (value) => {
      const text = typeof value === "string" ? value.trim() : "";
      if (!text) return "";
      if (text.toLowerCase() === "undefined") return "";
      return text;
    };

    let feedbackText = "";
    groupFeedback.forEach((item) => {
      if (!item?.feedback) return;

      if (!isOTP && item?.type === "comments") {
        const questionText = sanitizeQuestion(
          coreData?.questions?.comments?.[item?.id]?.question ??
          item?.feedback?.question
        );
        if (questionText) {
          feedbackText += `Question: ${questionText}\n`;
        }
        if (item?.feedback?.feedback) {
          feedbackText += `User feedback: ${item.feedback.feedback}\n`;
        }
      } else if (isOTP && item.type === "comments") {
        const questionText = sanitizeQuestion(item.feedback.question);
        if (questionText) {
          feedbackText += `Question: ${questionText}\n`;
        }
        if (item.feedback.response) {
          feedbackText += `User feedback: ${item.feedback.response}\n`;
        }
      } else if (
        !Number.isFinite(item.feedback.response) &&
        item?.type !== "outcome"
      ) {
        // Skip non-numeric responses unless it's an outcome type
        return;
      } else if ("prompt_feedback" in item.feedback) {
        const questionText = sanitizeQuestion(item.feedback.prompt_question);
        if (questionText) {
          feedbackText += `Follow-up question: ${questionText}\n`;
        }
        if (item.feedback.prompt_feedback) {
          feedbackText += `User feedback: ${item.feedback.prompt_feedback}\n`;
        }
      }
      // else if (
      //   typeof item.feedback.feedback === "string" &&
      //   item.feedback.feedback.length > 0
      // ) {
      //   console.log(item.feedback)
      //   feedbackText += `User feedback: ${item.feedback.feedback}\n`;
      // }

      if (item.feedback.follow_up) {
        feedbackText += `Follow-up: ${item.feedback.follow_up}\n`;
      }
      if (
        item.feedback?.response !== undefined &&
        item.type === "outcome"
      ) {
        feedbackText += `Outcome response: ${item.feedback.response}\n`;
      }

      feedbackText += "\n";
    });

    return feedbackText;
  };

  const appendFeedback = (
    originalFeedbackData = [],
    originalQuestionData = [],
    metaTag = "",
    options = {}
  ) => {
    const { selectedGroup = [], groupStructure = [], isOTP = false } = options;

    let allFilteredQuestions = [];
    let allFilteredFeedback = [];
    let feed = "";

    // Add initial feedback header if we have selected level 4
    if (selectedGroup.some((group) => group.level === 4)) {
      feed +=
        "The following feedback are responses to the question: " +
        selectedGroup.find((group) => group.level === 4)?.title +
        "\n\n";
    }

    // Combine the selectedFactors, selectedOutcomes, and selectedCustomQuestions
    const selectedAnalysis = [
      ...selectedFactors,
      ...selectedOutcomes.map((outcome) => ({
        title: outcome.title,
        id: outcome.id,
        level: 3,
        type: "outcome",
      })),
      ...(Array.isArray(selectedCustomQuestions[0])
        ? selectedCustomQuestions[0].map((question) => ({
            title: question.title,
            id: question?.id,
            level: 4,
            type: "comments",
          }))
        : selectedCustomQuestions.map((question) => ({
            title: question.title,
            id: question?.id,
            level: 4,
            type: "comments",
          }))),
    ];

    const defaultGroupStructure =
      selectedAnalysis?.length > 0
        ? selectedAnalysis
        : generateDefaultGroupStructure(coreData?.outcomeQuestions);

    const groupList = getGroupList(
      selectedAnalysis,
      defaultGroupStructure,
      defaultGroupStructure,
      isOTP
    );

    let totalFilteredLength = 0;

    const appendFallback = () => {
      const fallbackText = generateFeedbackText(
        originalFeedbackData,
        isOTP,
        coreData
      );

      if (fallbackText.trim()) {
        const fallbackCount = originalFeedbackData.filter(
          (item) => item?.feedback
        ).length;
        feed += fallbackText.endsWith("\n\n")
          ? fallbackText
          : `${fallbackText}\n\n`;
        totalFilteredLength = fallbackCount || originalFeedbackData.length;
      }
    };

    if (groupList.length === 0) {
      appendFallback();
      return { feed, totalFilteredLength };
    }

    groupList.forEach((group) => {
      // Skip Outcomes section entirely (level 3)
      if (group.level === 3) {
        return;
      }

      // Skip section headers without dimension (e.g., "Custom Questions", "Outcomes" headers)
      // These are level 1 items without a dimension property
      if (group.level === 1 && group.dimension === undefined) {
        return;
      }

      const { filteredFeedback, filteredQuestions } = filterByGroupLevel(
        group,
        originalFeedbackData,
        originalQuestionData
      );

      // Only process if we have feedback or questions
      if (filteredFeedback.length > 0 || filteredQuestions.length > 0) {
        // Calculate factor average
        const factorAverage = calculateFactorAverage(
          filteredQuestions.flatMap((q) => q.questions)
        );

        // Append group-level feedback (skip NaN/undefined scores)
        const scoreLabel =
          factorAverage == null || Number.isNaN(factorAverage)
            ? ""
            : ` - scored - ${factorAverage}%`;
        feed += `### ${group.title} ${metaTag} - ${group.title}${scoreLabel}:\n\n`;

        // Only generate feedback text for level 2 (factors) and level 4 (custom questions)
        // Skip feedback for level 0 (Overall Culture) and level 1 (Dimensions)
        if (group.level === 2 || group.level === 4) {
          feed += generateFeedbackText(filteredFeedback, isOTP, coreData);
        }

        feed += `### End of ${group.title} feedback\n\n`;

        allFilteredFeedback.push(...filteredFeedback);
        allFilteredQuestions.push(...filteredQuestions);
        totalFilteredLength += filteredFeedback.length;
      }
    });

    if (totalFilteredLength === 0 && originalFeedbackData.length > 0) {
      appendFallback();
    }

    return { feed, totalFilteredLength };
  };

  const getQuestionResponses = () => {
    let _data = [];
    const source = effectiveResponses;

    source
      ?.filter((f) => "questions" in f) // Check if 'questions' exists
      ?.filter((f) => f?.questions?.length > 0) // Ensure questions have data
      ?.map((item) => {
        // Push object with questions and categories
        _data.push({
          questions: item.questions, // Store questions
          categories: item.categories, // Include categories for context
        });
      });

    return _data;
  };

  const getFeedbackContext = () => {
    let _data = [];
    const filtered = effectiveResponses;

    if (!initialLoad && filtered.length > 0) setInitialLoad(true);
    if (filtered.length === 0) setEmptyData(true);
    else setEmptyData(false);

    // Handle regular feedback
    filtered
      ?.filter((item) => item.feedback?.length > 0)
      .forEach((item) =>
        item.feedback.forEach((f) =>
          _data.push({ feedback: f, categories: item.categories })
        )
      );

    // Handle feedback_builder responses
    filtered
      ?.filter((f) => "feedback_builder" in f)
      ?.filter((f) => f.feedback_builder?.responses?.length > 0)
      ?.forEach((item) => {
        item.feedback_builder.responses
          ?.filter((f) => f?.response)
          ?.forEach((f) => {
            _data.push({
              feedback: f?.responses || f,
              categories: item.categories,
              type: "outcome",
            });
          });
      });

    // Handle comments
    filtered
      ?.filter((f) => "comments" in f)
      ?.filter((f) => f.comments?.length > 0)
      ?.forEach((item) => {
        const comments = item.comments.filter((f) => {
          if (f?.feedback) {
            return f.feedback;
          } else if (f?.response) {
            return f?.response;
          }
        });

        const qData = activeQuestionStructure?.questions?.comments || [];

        comments.forEach((f, index) => {
          let correspondingQuestion = qData[index]; // Get the corresponding question from qData
          if (comments.length != qData.length) {
            correspondingQuestion = qData.find((q) =>
              q?.question?.toLowerCase()?.includes(f?.question?.toLowerCase())
            );
          }

          _data.push({
            feedback: f,
            categories: item.categories,
            type: "comments",
            id: correspondingQuestion
              ? correspondingQuestion.id || index
              : index,
          });
        });
      });

    return _data;
  };

  const flattenSurveyResponses = (survey) => {
    if (!Array.isArray(survey?.responses)) return [];

    return survey.responses
      .flatMap((group) => (Array.isArray(group?.responses) ? group.responses : []))
      .filter(Boolean);
  };

  // Process individual survey data with isolated calculations
  const processSurveyData = (survey) => {
    // Extract the actual responses from the nested structure
    const actualResponses = flattenSurveyResponses(survey);

    if (actualResponses.length === 0) {
      return "No survey responses available for processing.\\n\\n";
    }

    let surveyFeedback = "";

    try {
      // Process survey responses similar to existing logic but isolated
      const surveyContextData = extractSurveyFeedbackContext(actualResponses);
      const surveyQuestionData =
        extractSurveyQuestionResponses(actualResponses);

      if (surveyContextData.length === 0 && surveyQuestionData.length === 0) {
        surveyFeedback +=
          "No processable feedback data found in this survey.\\n\\n";
        return surveyFeedback;
      }

      // Apply the same factor/outcome analysis but for this survey only
      const surveyAnalysisResults = appendSurveyFeedback(
        surveyContextData,
        surveyQuestionData,
        survey.metadata
      );

      surveyFeedback += surveyAnalysisResults.feed;
    } catch (error) {
      console.error(`Error processing survey ${survey.id}:`, error);
      surveyFeedback += `Error processing survey data: ${error.message}\\n\\n`;
    }

    return surveyFeedback;
  };

  // Extract feedback context from survey responses (similar to getFeedbackContext but for specific survey)
  const extractSurveyFeedbackContext = (responses) => {
    let _data = [];

    responses.forEach((responseItem, index) => {
      // Extract the actual response data
      const response = responseItem.response;
      const employeeId = responseItem.employee_id;

      // Handle regular feedback from survey response
      if (response.feedback?.length > 0) {
        response.feedback.forEach((f) =>
          _data.push({
            feedback: f,
            categories: response.categories,
            surveyId: employeeId,
          })
        );
      }

      // Handle feedback_builder responses
      if (response.feedback_builder?.responses?.length > 0) {
        response.feedback_builder.responses
          .filter((f) => f?.response)
          .forEach((f) => {
            _data.push({
              feedback: f?.responses || f,
              categories: response.categories,
              type: "outcome",
              surveyId: employeeId,
            });
          });
      }

      // Handle employee_outcomes responses
      if (response.employee_outcomes?.responses?.length > 0) {
        response.employee_outcomes.responses
          .filter((f) => f?.response)
          .forEach((f) => {
            _data.push({
              feedback: f?.responses || f,
              categories: response.categories,
              type: "outcome",
              surveyId: employeeId,
            });
          });
      }

      // Handle comments
      if (response.comments?.length > 0) {
        const comments = response.comments.filter((f) => {
          return f?.feedback || f?.response;
        });

        const qData = activeQuestionStructure?.questions?.comments || [];

        comments.forEach((f, commentIndex) => {
          let correspondingQuestion = qData[commentIndex];
          if (comments.length !== qData.length) {
            correspondingQuestion = qData.find((q) =>
              q?.question?.toLowerCase()?.includes(f?.question?.toLowerCase())
            );
          }

          _data.push({
            feedback: f,
            categories: response.categories,
            type: "comments",
            id: correspondingQuestion
              ? correspondingQuestion.id || commentIndex
              : commentIndex,
            surveyId: employeeId,
          });
        });
      }
    });

    return _data;
  };

  // Extract question responses from survey responses (similar to getQuestionResponses but for specific survey)
  const extractSurveyQuestionResponses = (responses) => {
    let _data = [];

    responses.forEach((responseItem) => {
      const response = responseItem.response;
      const employeeId = responseItem.employee_id;

      if (response.questions && response.questions.length > 0) {
        _data.push({
          questions: response.questions,
          categories: response.categories,
          surveyId: employeeId,
        });
      }
    });

    return _data;
  };

  // Process survey feedback with analysis (similar to appendFeedback but for specific survey)
  const appendSurveyFeedback = (
    surveyContextData,
    surveyQuestionData,
    metadata
  ) => {
    let feed = "";
    let totalLength = 0;

    // Combine the selectedFactors, selectedOutcomes, and selectedCustomQuestions for this survey
    const selectedAnalysis = [
      ...selectedFactors,
      ...selectedOutcomes.map((outcome) => ({
        title: outcome.title,
        id: outcome.id,
        level: 3,
        type: "outcome",
      })),
      ...(Array.isArray(selectedCustomQuestions[0])
        ? selectedCustomQuestions[0].map((question) => ({
            title: question.title,
            id: question?.id,
            level: 4,
            type: "comments",
          }))
        : selectedCustomQuestions.map((question) => ({
            title: question.title,
            id: question?.id,
            level: 4,
            type: "comments",
          }))),
    ];

    const defaultGroupStructure =
      selectedAnalysis?.length > 0
        ? selectedAnalysis
        : generateDefaultGroupStructure(coreData?.outcomeQuestions);

    // Process each group for this specific survey
    defaultGroupStructure.forEach((group) => {
      // Skip Outcomes section entirely (level 3)
      if (group.level === 3) {
        return;
      }

      // Skip section headers without dimension (e.g., "Custom Questions", "Outcomes" headers)
      if (group.level === 1 && group.dimension === undefined) {
        return;
      }

      const { filteredFeedback, filteredQuestions } =
        filterSurveyDataByGroupLevel(
          group,
          surveyContextData,
          surveyQuestionData
        );

      // Only process if we have feedback or questions
      if (filteredFeedback.length > 0 || filteredQuestions.length > 0) {
        // Calculate factor average
        const factorAverage = calculateFactorAverage(
          filteredQuestions.flatMap((q) => q.questions)
        );

        const scoreLabel =
          factorAverage == null || Number.isNaN(factorAverage)
            ? ""
            : ` - scored - ${factorAverage}%`;
        feed += `### ${group.title}${scoreLabel}:\\n\\n`;

        // Only generate feedback text for level 2 (factors) and level 4 (custom questions)
        if (group.level === 2 || group.level === 4) {
          feed += generateFeedbackText(filteredFeedback, false, coreData);
        }

        feed += `### End of ${group.title} feedback\\n\\n`;

        totalLength += filteredFeedback.length;
      }
    });

    return { feed, totalLength };
  };

  // Filter survey data by group level (similar to filterByGroupLevel but for specific survey)
  const filterSurveyDataByGroupLevel = (
    group,
    surveyFeedback,
    surveyQuestions
  ) => {
    let filteredFeedback = [...surveyFeedback];
    let filteredQuestions = [...surveyQuestions];

    switch (group.level) {
      case 0:
        // Overall Culture - no specific filtering needed since we skip feedback generation
        break;
      case 1:
        filteredFeedback = filteredFeedback.filter(
          (f) =>
            f.feedback.id === group.dimension ||
            f.feedback?.dimension === group.dimension
        );
        filteredQuestions = filteredQuestions.filter((q) =>
          q.questions.some((question) => question.id === group.dimension)
        );
        break;
      case 2:
        filteredFeedback = filteredFeedback.filter(
          (f) =>
            (f.feedback.id === group.dimension ||
              f.feedback?.dimension === group.dimension) &&
            f.feedback.factor === group.factor
        );
        filteredQuestions = filteredQuestions.map((q) => ({
          ...q,
          questions: q.questions.filter(
            (question) =>
              question.id === group.dimension &&
              question.factor === group.factor
          ),
        }));
        break;
      case 3:
        filteredFeedback = filteredFeedback.filter(
          (f) =>
            f.type === "outcome" && Number(f.feedback.q) === Number(group.id)
        );
        filteredQuestions = filteredQuestions.filter((q) =>
          q.questions.some(
            (question) => Number(question.q) === Number(group.id)
          )
        );
        break;
      case 4:
        // Handle custom questions (level 4)
        filteredFeedback = filteredFeedback.filter(
          (f) => f.type === "comments" && f.id === group.id
        );

        filteredQuestions = filteredQuestions.filter((q) =>
          q.questions.some((question) => question.q === group.id)
        );
        break;
      default:
        break;
    }

    return { filteredFeedback, filteredQuestions };
  };

  // Process already-filtered multi-survey data (array of objects: {feedback, questions, comments, categories, ...})
  const processFilteredData = (data) => {
    // Some reducers hand back [[...]] so normalize first
    const filtered = data?.[0]?.[0] ? data[0] : data;
    if (!Array.isArray(filtered) || filtered.length === 0) {
      return { feedback: "", totalLength: 0 };
    }

    // Check if this is the new filteredData structure with selectedFilters
    // Each item should have a feedback array that we need to compile
    if (selectedFilters?.length > 0 && filtered[0]?.feedback) {
      let compiledFeedback = "";
      let totalFeedbackItems = 0;

      filtered.forEach((item) => {
        if (Array.isArray(item.feedback) && item.feedback.length > 0) {
          item.feedback.forEach((feedbackItem) => {

            // Check if this feedback item has actual feedback content
            if (feedbackItem.feedback && feedbackItem.feedback.trim() !== "" && feedbackItem.feedback !== "N/A") {
              compiledFeedback += `Question: ${feedbackItem.question}\n`;
              compiledFeedback += `User feedback: ${feedbackItem.feedback}\n`;
              
              // Add NLP classification if available
              if (feedbackItem.nlp_classification) {
                compiledFeedback += `Sentiment: ${feedbackItem.nlp_classification.sentiment}\n`;
                if (feedbackItem.nlp_classification.action_orientation) {
                  compiledFeedback += `Action Orientation: ${feedbackItem.nlp_classification.action_orientation}\n`;
                }
              }
              
              compiledFeedback += "\n";
              totalFeedbackItems++;
            }
            
            // Handle response field as feedback if it's text and not in feedback field
            if (feedbackItem.response && typeof feedbackItem.response === 'string' && 
                feedbackItem.response.trim() !== "" && feedbackItem.response !== "N/A" &&
                (!feedbackItem.feedback || feedbackItem.feedback.trim() === "")) {
              compiledFeedback += `Question: ${feedbackItem.question}\n`;
              compiledFeedback += `User feedback: ${feedbackItem.response}\n`;
              
              // Add NLP classification if available
              if (feedbackItem.nlp_classification) {
                compiledFeedback += `Sentiment: ${feedbackItem.nlp_classification.sentiment}\n`;
                if (feedbackItem.nlp_classification.action_orientation) {
                  compiledFeedback += `Action Orientation: ${feedbackItem.nlp_classification.action_orientation}\n`;
                }
              }
              
              compiledFeedback += "\n";
              totalFeedbackItems++;
            }
          });
        }
      });

      const header = `=== FILTERED DATA WITH APPLIED FILTERS ===\n` +
                    `Filters applied: ${selectedFilters.length} filter(s)\n` +
                    `Total feedback items: ${totalFeedbackItems}\n\n`;

      return { feedback: header + compiledFeedback, totalLength: totalFeedbackItems };
    }

    // Fallback to original logic for other data structures
    // Build the two structures appendFeedback expects
    const contextData = [];
    const questionData = [];

    // Regular feedback
    filtered
      ?.filter(
        (item) => Array.isArray(item.feedback) && item.feedback.length > 0
      )
      .forEach((item) =>
        item.feedback.forEach((f) =>
          contextData.push({ feedback: f, categories: item.categories })
        )
      );

    // feedback_builder responses (treated like outcomes)
    filtered
      ?.filter((f) => "feedback_builder" in f)
      ?.filter((f) => f.feedback_builder?.responses?.length > 0)
      ?.forEach((item) => {
        item.feedback_builder.responses
          ?.filter((f) => f?.response)
          ?.forEach((f) => {
            contextData.push({
              feedback: f?.responses || f,
              categories: item.categories,
              type: "outcome",
            });
          });
      });

    // employee_outcomes responses (also outcomes)
    filtered
      ?.filter((f) => "employee_outcomes" in f)
      ?.filter((f) => f.employee_outcomes?.responses?.length > 0)
      ?.forEach((item) => {
        item.employee_outcomes.responses
          ?.filter((f) => f?.response)
          ?.forEach((f) => {
            contextData.push({
              feedback: f?.responses || f,
              categories: item.categories,
              type: "outcome",
            });
          });
      });

    // Comments
    filtered
      ?.filter((f) => "comments" in f)
      ?.filter((f) => Array.isArray(f.comments) && f.comments.length > 0)
      ?.forEach((item) => {
        const comments = item.comments.filter(
          (f) => f?.feedback || f?.response
        );
        const qData = activeQuestionStructure?.questions?.comments || [];

        comments.forEach((f, index) => {
          let correspondingQuestion = qData[index];
          if (comments.length !== qData.length) {
            correspondingQuestion = qData.find((q) =>
              q?.question?.toLowerCase()?.includes(f?.question?.toLowerCase())
            );
          }

          contextData.push({
            feedback: f,
            categories: item.categories,
            type: "comments",
            id: correspondingQuestion
              ? correspondingQuestion.id || index
              : index,
          });
        });
      });

    // Questions (for factor averages)
    filtered
      ?.filter((f) => Array.isArray(f.questions) && f.questions.length > 0)
      ?.forEach((item) => {
        questionData.push({
          questions: item.questions,
          categories: item.categories,
        });
      });

    const surveyNames = (selectedSurveys || [])
      .map((s) => s?.metadata?.surveyName)
      .filter(Boolean);

    const header =
      `=== FILTERED MULTI-SURVEY DATA (${filtered.length} records) ===\n` +
      (surveyNames.length
        ? `Surveys included: ${surveyNames.join(", ")}\n`
        : "") +
      `Filters applied: yes (showing only currently filtered responses)\n\n`;

    const { feed, totalFilteredLength } = appendFeedback(
      contextData,
      questionData
    );

    return { feedback: header + feed, totalLength: totalFilteredLength };
  };

  // Process feedback for multiple surveys with separate calculations
  const processMultiSurveyFeedback = (filteredData, surveysToProcess = selectedSurveys) => {
    if (
      surveysToProcess.length === 0 &&
      !(filteredData?.length > 0 && selectedFilters?.length > 0)
    ) {
      return { feedback: "", totalLength: 0 };
    }

    // Prefer filteredData when filters are applied
    if (filteredData?.length > 0 && selectedFilters?.length > 0) {
      return processFilteredData(filteredData);
    }

    // Fallback: iterate selected surveys one by one
    let compiledFeedback = "";
    let totalLength = 0;

    surveysToProcess.forEach((survey, index) => {
      const surveyHeader = `\n\n=== SURVEY DATA ${index + 1} ===\n`;

      // Format survey name with fallbacks
      const surveyName = survey.metadata?.surveyName || survey.title || "Survey";

      // Format completion date properly
      let completionDate = "N/A";
      if (survey.metadata?.completionDate) {
        const timestamp = survey.metadata.completionDate;
        if (typeof timestamp === 'number') {
          completionDate = new Date(timestamp).toLocaleDateString();
        } else if (typeof timestamp === 'string') {
          const date = new Date(timestamp);
          completionDate = !isNaN(date.getTime()) ? date.toLocaleDateString() : timestamp;
        }
      }

      // Only include category if it exists and is not undefined
      const categoryInfo = survey.metadata?.category
        ? `(${survey.metadata.category}) ` 
        : survey.metadata?.isIndividual
          ? "(Individual Survey) " 
        : "";

      const surveyMetadata = `This data relates to survey "${surveyName}" ${categoryInfo}which was completed on ${completionDate}\n\n`;

      compiledFeedback += surveyHeader + surveyMetadata;

      const surveyFeedback = processSurveyData(survey);
      compiledFeedback += surveyFeedback;

      compiledFeedback += `=== END OF SURVEY DATA ${index + 1} ===\n\n`;

      const actualResponseCount = flattenSurveyResponses(survey).length;
      totalLength += actualResponseCount;
    });

    return { feedback: compiledFeedback, totalLength };
  };

  useEffect(() => {
    const canCompileOtpSelectionsWithoutCoreData =
      isOtpAiRoute &&
      (selectedPulses.length > 0 || selectedSurveys.length > 0);

    // OTP direct-entry can still serialize manually selected survey and pulse data
    // even before the full analytics bootstrap has populated coreData.
    if (!coreData && !canCompileOtpSelectionsWithoutCoreData) return;

    // Check if we have existing data loaded from NewAnalytics
    const hasExistingData = hasCustomDataset;

    let compiledFeedback = "";
    let totalFilteredLength = 0;

    // Log which data source we're using and add context to feedback
    if (hasExistingData) {
      const metadata = existingRawData[0].datasetMetadata;
      // Add metadata context to the feedback
      compiledFeedback += `=== DATA SOURCE CONTEXT ===\n`;
      compiledFeedback += `This analysis is based on survey data from: "${metadata.surveyName}"\n`;
      if (metadata.groupTitle) {
        compiledFeedback += `Group/Category: ${metadata.groupTitle}\n`;
      }
      compiledFeedback += `Data loaded on: ${new Date(
        metadata.timestamp
      ).toLocaleDateString()}\n`;
      compiledFeedback += `Selection type: ${metadata.selectionType}\n`;
      const totalResponseCount =
        effectiveResponses.length > 0
          ? effectiveResponses.length
          : metadata.originalFilteredCount || 0;
      compiledFeedback += `Total responses: ${totalResponseCount}\n`;
      compiledFeedback += `=== END CONTEXT ===\n\n`;
    }

    // Handle pulse survey selection - now supporting multiple pulses
    if (selectedPulses.length > 0) {
      selectedPulses.forEach((selectedPulse, index) => {
        const pulseDetails = fetchedPulsesData[selectedPulse.id];
        if (pulseDetails) {
          const pulseFeedback = parsePulseSurveyResponses(pulseDetails);
          if (
            pulseFeedback &&
            pulseFeedback.trim() !== "No task responses found."
          ) {
            const pulseHeader = selectedPulses.length > 1
              ? `=== PULSE SURVEY DATA ${index + 1}: ${selectedPulse.title} ===\n\n`
              : `=== PULSE SURVEY DATA ===\n\n`;
            compiledFeedback += pulseHeader + pulseFeedback + `\n\n=== END OF PULSE SURVEY DATA ${selectedPulses.length > 1 ? index + 1 : ''} ===\n\n`;
            totalFilteredLength += pulseDetails?.task_responses?.length || 0;
          }
        }
      });
    }

    // Handle multi-survey selection (for manually selected surveys, not custom datasets)
    if (selectedSurveys.length > 0 && !hasExistingData) {
      const surveyResults = processMultiSurveyFeedback(
        normalizedFilteredData,
        selectedSurveys
      );
      compiledFeedback += surveyResults.feedback;
      totalFilteredLength += surveyResults.totalLength;
    }

    // Determine if we should include regular survey data processing
    // Skip regular data if ONLY pulse surveys are selected (no factors, outcomes, or custom questions)
    const hasNonPulseDataSelection =
      selectedFactors.length > 0 ||
      selectedOutcomes.length > 0 ||
      selectedCustomQuestions.length > 0 ||
      selectedSurveys.length > 0 ||
      hasExistingData;

    // Handle regular data processing for factors/outcomes/custom questions AND SCORES
    // Only process if we have filtered data OR if we have existing data with metadata
    // AND we're not in a pulse-only selection mode
    if (
      coreData &&
      (effectiveResponses.length > 0 || hasExistingData) &&
      hasNonPulseDataSelection
    ) {
      const contextData = getFeedbackContext();
      const questionData = getQuestionResponses();

      // Append regular feedback using the existing function (includes scores, factors, outcomes)
      const { feed: regularFeedback, totalFilteredLength: regularLength } =
        appendFeedback(contextData, questionData);
      
      // Add regular feedback - this includes quantitative scores from survey responses
      if (regularFeedback.trim()) {
        // Only add "ADDITIONAL ANALYSIS DATA" header if we have manually selected surveys
        if (selectedSurveys.length > 0 && !hasExistingData) {
          compiledFeedback += "\\n\\n=== ADDITIONAL ANALYSIS DATA ===\\n\\n";
        }
        compiledFeedback += regularFeedback;
        totalFilteredLength += regularLength;
      }
    }

    // If we still don't have any feedback but have existing data, there might be a processing issue
    if (!compiledFeedback.trim() && hasExistingData) {
      compiledFeedback =
        "Data source has been selected but no feedback could be compiled. This may indicate an issue with data processing.";
      totalFilteredLength = 1; // Set to 1 to indicate data exists but couldn't be processed
    }

    setFeedback(compiledFeedback);

    if (selectedPulses.length > 0) {
      // Calculate total responses from all selected pulses
      const totalPulseResponses = selectedPulses.reduce((total, pulse) => {
        const pulseDetails = fetchedPulsesData[pulse.id];
        return total + (pulseDetails?.task_responses?.length || 0);
      }, 0);

      // If the user has selected a pulse, make sure the feedback length isn't completely removed.
      if (totalFilteredLength === 0) {
        setFeedbackLength(totalPulseResponses);
      } else {
        // If the user has selected an outcome or factor or custom question,
        // We need to add the length of the task_responses to the feedback length.
        // Use the higher value between pulse length and current filtered length
        setFeedbackLength(Math.max(totalPulseResponses, totalFilteredLength));
      }
    } else {
      setFeedbackLength(totalFilteredLength);
    }

    setFeedbackLoading(false);
  }, [
    normalizedFilteredData,
    effectiveResponses,
    coreData,
    existingRawData, // Add dependency for existing data
    hasCustomDataset,
    activeQuestionStructure,
    selectedFactors,
    selectedOutcomes,
    selectedPulses,
    fetchedPulsesData, // Track all fetched pulse data
    selectedCustomQuestions,
    selectedSurveys,
    selectedFilters,
    fetchedPulse,
    isOtpAiRoute,
  ]);

  // ----------------------------------
  // Expose a param-builder for the Chat
  // ----------------------------------
  // If you'd like to add more fields (like "tags"), you can do so,
  // or pull them from another context if needed.
  const getFeedbackParams = (fileSources = [], promptObject) => {
    const metadata =
      fileSources?.length > 0
        ? fileSources?.map((source) => ({
            id: source.id,
            type: "file",
            name: source.name,
          }))
        : [];

    const tags = selectedFactors?.map((factor) => factor.title);
    // Add outcomes to the tags
    const outcomes = selectedOutcomes?.map((outcome) => outcome.title);
    const pulseData =
      selectedPulses?.map((pulse) => ({
        id: pulse.id,
        type: "pulse",
        title: pulse.title,
      })) || [];

    // Add survey data sources to params
    const surveyData =
      selectedSurveys?.map((survey) => ({
        id: survey.id,
        type: "survey",
        title: survey.title,
        name: survey.title,
        surveysCount: survey.surveysCount,
        lastUpdated: survey.lastUpdated,
        metadata: survey.metadata,
      })) || [];

    // Add custom questions to params
    const customQuestions = selectedCustomQuestions?.map(
      (question) => question.title
    );

    const promptData = promptObject
      ? {
          id: promptObject?.id,
          type: "prompt",
          question: promptObject?.categoryContent?.props?.prompt?.ques,
          prompt: promptObject?.categoryContent?.props?.prompt?.prompt,
          title: promptObject?.categoryTitle,
        }
      : null;

    const dataSources = [
      ...pulseData,
      ...surveyData,
      ...metadata,
      ...(promptData ? [promptData] : []),
    ].filter(Boolean);
    return {
      feedback_length: feedbackLength,
      date: "",
      // This is an expected param, do not overwrite
      context_data_length: 0,
      // If you do store 'tags' or 'factors' here, you can add them:
      tags,
      dataSources,
      outcomes,
      customQuestions,
    };
  };

  const handleOutcomeSelection = (outcomeId) => {
    const selectedOutcome = outcomeCardData.find((o) => o.id === outcomeId);
    if (selectedOutcome) {
      setOutcomeCardData(
        outcomeCardData.map((o) =>
          o.id === outcomeId ? { ...o, selected: !o.selected } : o
        )
      );
    }
  };

  // For viewing extracted data.
  // console.log(feedback);

  return (
    <FeedbackContext.Provider
      value={{
        feedback,
        feedbackLength,
        feedbackLoading,
        emptyData,
        initialLoad,
        getFeedbackParams,
        fetchedPulse,
        pulseSurveys: filteredPulses,
        setSelectedPulse,
        pulseData,
        outcomeQ: coreData?.outcomeQuestions,
        factorLabels,
        setSelectedFactors,
        setSelectedOutcomes,
        handleSelectPulse,
        handleRemovePulse,
        selectedPulses,
        selectedFactors,
        selectedOutcomes,
        factorCardData,
        outcomeCardData,
        handleRemoveFactor,
        handleRemoveOutcome,
        newChatClicked,
        handleRemoveAllFactors,
        handleRemoveAllOutcomes,
        customQuestions: getCustomQuestions,
        selectedCustomQuestions,
        setSelectedCustomQuestions,
        handleSelectCustomQuestion,
        handleRemoveCustomQuestion,
        handleRemoveAllCustomQuestions,
        customQuestionsCardData,
        selectedSurveys,
        surveyCardData,
        handleSelectSurvey,
        handleRemoveSurvey,
        handleRemoveAllSurveys,
        effectiveResponses,
        coreData,
      }}
    >
      {children}
    </FeedbackContext.Provider>
  );
};
